import { axiosInstance } from '../../../utils/tokenUtils';
import { getToken } from '../../../utils/tokenUtils';
import Cookies from 'js-cookie';

const sockets = new Map();
const reconnectAttempts = new Map();

const MAX_RECONNECT_ATTEMPTS = 3;
const PING_INTERVAL_MS = 25_000;
const MAX_RECONNECT_DELAY_MS = 30_000;

const WS_STATES = {
  0: 'CONNECTING',
  1: 'OPEN',
  2: 'CLOSING',
  3: 'CLOSED',
};

const logWsState = (chatId, state, extra = '') => {
  const stateStr = WS_STATES[state] || 'UNKNOWN';
  const msg = `[WS ${chatId}] ${stateStr}${extra ? ` - ${extra}` : ''}`;

  if (state === 1) {
    console.log('%c' + msg, 'color: #4CAF50; font-weight: bold');
  } else if (state === 2 || state === 3) {
    console.log('%c' + msg, 'color: #F44336; font-weight: bold');
  } else {
    console.log('%c' + msg, 'color: #2196F3; font-weight: bold');
  }
};

const isTokenExpired = token => {
  try {
    const { exp } = JSON.parse(atob(token.split('.')[1]));
    return Date.now() / 1_000 >= exp;
  } catch {
    return true;
  }
};

const buildWsUrl = (chatId, token) => {
  const explicit = process.env.REACT_APP_WS_BASE_URL;
  const restBase = process.env.REACT_APP_SERVER_API_URL;

  const toWsBase = base => {
    if (!base) return null;
    const withProto = /^(https?:|wss?:)/.test(base) ? base : `https://${base}`;
    const url = new URL(withProto);
    const scheme = url.protocol === 'https:' || url.protocol === 'wss:' ? 'wss' : 'ws';
    return `${scheme}://${url.host}`;
  };

  let wsBase = toWsBase(explicit);

  if (!wsBase) wsBase = toWsBase(restBase);

  if (!wsBase) wsBase = toWsBase(window.location.origin);

  return `${wsBase}/chat/ws/${chatId}?token=${token}`;
};

const logFailedHandshake = async (wsUrl, error) => {
  try {
    console.groupCollapsed(`Could not connect to ${wsUrl}`);
    console.error('Error:', error?.message || error);
    console.info('Handshake Details');
    console.groupEnd();
  } catch (e) {
    console.groupCollapsed(`Could not connect to ${wsUrl}`);
    console.error('Error:', error?.message || error);
    console.info('Additional handshake probe failed:', e);
    console.groupEnd();
  }
};

class ChatService {
  static async getTeacherChatList() {
    try {
      const { data } = await axiosInstance.get(`/chat/teacher`);
      return data;
    } catch (error) {
      console.error('Error in getTeacherChatList:', error);
      throw error;
    }
  }

  static async getChatMessages(chatId, limit = 50, offset = 0) {
    try {
      const { data } = await axiosInstance.get(`/chat/${chatId}?limit=${limit}&offset=${offset}`);

      const messagesWithLocalTime = data.map(message => {
        if (!message.created_at) return message;

        const utcDate = new Date(message.created_at);

        const localDate = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000);

        const localDateString = localDate.toISOString().replace('Z', '');

        return {
          ...message,
          created_at: localDateString,
        };
      });

      return messagesWithLocalTime;
    } catch (error) {
      console.error('Error in getChatMessages:', error);
      throw error;
    }
  }

  static async searchStudents(searchTerm) {
    try {
      const { data } = await axiosInstance.get(`/chat/search/students?search_term=${searchTerm}`);
      return data;
    } catch (error) {
      console.error('Error in searchStudents:', error);
      throw error;
    }
  }

  static async markMessageAsRead(messageId) {
    try {
      const { data } = await axiosInstance.patch(`/chat/message/${messageId}/read`);
      return data;
    } catch (error) {
      console.error('Error in markMessageAsRead:', error);
      throw error;
    }
  }

  static async markAllAsRead(chatId) {
    try {
      const { data } = await axiosInstance.patch(`/chat/${chatId}/read`);
      return data;
    } catch (error) {
      console.error('Error in markAllAsRead:', error);
      throw error;
    }
  }

  /**
   * Upload a file as a chat message
   * @param {string|number} chatId
   * @param {File|Blob} file
   * @param {string|number} recipientId
   * @returns {Promise<any>}
   */
  static async uploadChatFile(chatId, file, recipientId) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('recipient_id', recipientId);

      const { data } = await axiosInstance.post(`/chat/${chatId}/files`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      // Normalize possible shapes
      const normalized = {
        id: data?.id ?? data?.message_id ?? data?.message?.id,
        message_id: data?.message_id ?? data?.id ?? data?.message?.id,
        message: data?.message ?? data?.content ?? data?.text ?? '',
        sender_id: data?.sender_id ?? data?.sender?.id, // may be undefined for upload response
        recipient_id: data?.recipient_id ?? data?.recipient?.id ?? recipientId,
        created_at: data?.created_at ?? data?.timestamp ?? new Date().toISOString(),
        file_url: data?.file_url ?? data?.file?.url ?? data?.url,
        file_name: data?.file_name ?? data?.file?.name ?? file?.name,
        file_size: data?.file_size ?? data?.file?.size ?? file?.size,
      };
      return normalized;
    } catch (error) {
      console.error('Error in uploadChatFile:', error);
      throw error;
    }
  }

  /**
   * @param {string}   chatId
   * @param {Function} onMessageCb
   * @returns {WebSocket|null}
   */
  static connectWebSocket(chatId, onMessageCb) {
    const existing = sockets.get(chatId);
    if (existing && existing.readyState === WebSocket.OPEN) {
      logWsState(chatId, 1, 'Reusing existing connection');
      existing.__onMsg = onMessageCb;
      return existing;
    }

    logWsState(chatId, 0, 'Initiating new connection');

    const attempts = reconnectAttempts.get(chatId) ?? 0;
    if (attempts >= MAX_RECONNECT_ATTEMPTS) {
      logWsState(chatId, 3, `Gave up after ${attempts} failed attempts`);
      return null;
    }
    reconnectAttempts.set(chatId, attempts + 1);

    const token = getToken() || Cookies.get('access_token') || Cookies.get('refresh_token');
    if (!token || isTokenExpired(token)) {
      logWsState(chatId, 3, 'No valid auth token - aborting connection');
      return null;
    }

    const wsUrl = buildWsUrl(chatId, token);

    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch (err) {
      logWsState(chatId, 3, `Constructor error: ${err.message}`);
      logFailedHandshake(wsUrl, err);
      return null;
    }

    ws.onopen = () => {
      logWsState(chatId, 1);
      reconnectAttempts.set(chatId, 0);

      try {
        console.groupCollapsed(`Connected to ${wsUrl}`);

        console.groupEnd();
      } catch (_) {}

      ws.__ping = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, PING_INTERVAL_MS);
    };

    ws.onmessage = evt => {
      try {
        const data = JSON.parse(evt.data);
        if (typeof onMessageCb === 'function') onMessageCb(data);
      } catch (err) {
        console.error(`[WS ${chatId}] Message parse error:`, err, evt.data);
      }
    };

    ws.onerror = evt => {
      logWsState(chatId, ws.readyState, `Error event: ${evt.type}`);
      logFailedHandshake(wsUrl, evt);
    };

    ws.onclose = evt => {
      clearInterval(ws.__ping);
      sockets.delete(chatId);
      logWsState(chatId, 3, `Code=${evt.code} Reason=${evt.reason || '-'}`);

      const tries = reconnectAttempts.get(chatId) ?? 0;
      if (tries < MAX_RECONNECT_ATTEMPTS) {
        const delay = Math.min(1000 * 2 ** tries, MAX_RECONNECT_DELAY_MS);
        console.info(
          `[WS ${chatId}] Reconnecting in ${delay}ms (attempt ${tries + 1}/${MAX_RECONNECT_ATTEMPTS})`
        );
        setTimeout(() => ChatService.connectWebSocket(chatId, onMessageCb), delay);
      }
    };

    sockets.set(chatId, ws);
    ws.__onMsg = onMessageCb;
    return ws;
  }

  static sendMessage(chatId, content, recipientId) {
    const ws = sockets.get(chatId);
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      logWsState(chatId, ws?.readyState || 3, 'Cannot send message - socket not open');
      return false;
    }
    ws.send(JSON.stringify({ content, recipient_id: recipientId }));
    return true;
  }

  static closeWebSocket(chatId) {
    const ws = sockets.get(chatId);
    if (ws) {
      try {
        ws.close(1000, 'client-close');
      } catch {}
      clearInterval(ws.__ping);
      sockets.delete(chatId);
      reconnectAttempts.delete(chatId);
      logWsState(chatId, 3, 'Manually closed');
    }
  }
}

export default ChatService;
